// Chat Context for global chat state management
// Provides chat functionality across the application

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  ChatContextType,
  ConversationListItem,
  ConversationMessagesResponse,
  CreateConversationResponse,
  ChatStats,
  ChatMessage
} from '../types/Chat';
import {
  createConversation,
  getUserConversations,
  markConversationAsRead,
  ChatApiError,
  getFirebaseConfig
} from '../services/chatApi';
import {
  subscribeToMessages,
  subscribeToTypingIndicators,
  sendRealtimeMessage,
  setTypingStatus,
  markAllMessagesAsRead,
  cleanupAllListeners,
  setUserOnlineStatus,
  initializeFirebaseConversation,
  initializeFirebaseChat,
  updateParticipantPresence
} from '../services/firebaseChat';

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // State
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ConversationMessagesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ChatStats | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const isLoadingRef = useRef(false);
  const initializingConversationRef = useRef<string | null>(null);
  
  // Real-time state
  const [realtimeMessages, setRealtimeMessages] = useState<ChatMessage[]>([]);
  // Typing indicators are handled via Firebase; we currently don't store them in UI state
  
  // Cleanup functions for Firebase listeners
  const [messageUnsubscribe, setMessageUnsubscribe] = useState<(() => void) | null>(null);
  const [typingUnsubscribe, setTypingUnsubscribe] = useState<(() => void) | null>(null);

  // Initialize Firebase Chat when user is authenticated
  useEffect(() => {
    if (user && !firebaseInitialized) {
      const initFirebase = async () => {
        try {
          console.log('🔧 Initializing Firebase Chat with backend config...');
          const firebaseConfig = await getFirebaseConfig();
          await initializeFirebaseChat(firebaseConfig);
          setFirebaseInitialized(true);
          console.log('✅ Firebase Chat initialized successfully');
        } catch (error) {
          console.error('❌ Failed to initialize Firebase Chat:', error);
          // Fallback to default Firebase configuration
          await initializeFirebaseChat();
          setFirebaseInitialized(true);
        }
      };

      initFirebase();
      setUserOnlineStatus(true);
      
      // Set offline when component unmounts or user logs out
      return () => {
        setUserOnlineStatus(false);
      };
    }
  }, [user, firebaseInitialized]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      cleanupAllListeners();
    };
  }, []);

  /**
   * Load user conversations
   */
  const loadConversations = useCallback(async () => {
    if (!user) return;

    // Guard against duplicate concurrent loads without tying to state changes
    if (isLoadingRef.current) {
      console.warn('⚠️ Conversations already loading, skipping duplicate call');
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await getUserConversations();
      setConversations(response.conversations);
      console.log(`✅ Loaded ${response.conversations.length} conversations`);
    } catch (error) {
      const errorMessage = error instanceof ChatApiError 
        ? error.message 
        : 'Failed to load conversations';
      setError(errorMessage);
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [user]);

  /**
   * Create a new conversation (activity with organizer, or profile DM via recipientUserId)
   */
  const createConversationHandler = useCallback(async (
    activityIdOrOptions: number | { recipientUserId: string; initialMessage?: string },
    initialMessage?: string
  ): Promise<CreateConversationResponse> => {
    if (!user) {
      throw new ChatApiError('ACCESS_DENIED', 'User not authenticated');
    }

    if (!firebaseInitialized) {
      throw new ChatApiError('CONNECTION_ERROR', 'Firebase Chat not initialized');
    }

    setLoading(true);
    setError(null);

    try {
      const response =
        typeof activityIdOrOptions === 'number'
          ? await createConversation(activityIdOrOptions, initialMessage)
          : await createConversation(activityIdOrOptions);
      
      // Initialize Firebase conversation structure using new response format
      try {
        if (response.conversation_id && response.participants) {
          await initializeFirebaseConversation(
            response.conversation_id,
            response.activity?.id ?? 0,
            response.participants
          );
          console.log('✅ Firebase conversation initialized with new structure');
        }
      } catch (firebaseError) {
        console.warn('⚠️ Firebase conversation initialization failed (continuing anyway):', firebaseError);
        // Don't throw here - the backend conversation was created successfully
      }
      
      // Refresh conversations list to include the new one
      await loadConversations();
      
      console.log('✅ Conversation created successfully');
      return response;
    } catch (error) {
      const errorMessage = error instanceof ChatApiError 
        ? error.message 
        : 'Failed to create conversation';
      setError(errorMessage);
      console.error('Error creating conversation:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user, firebaseInitialized, loadConversations]);

  /**
   * Open a conversation and subscribe to real-time updates
   */
  const openConversation = useCallback(async (conversationId: string) => {
    if (!user) return;

    // Prevent multiple simultaneous calls for the same conversation
    if (initializingConversationRef.current === conversationId) {
      console.warn('⚠️ Conversation already initializing, skipping duplicate call for:', conversationId);
      return;
    }

    // Also check general initialization state
    if (isInitializing && initializingConversationRef.current) {
      console.warn('⚠️ Another conversation is initializing, skipping duplicate call');
      return;
    }

    // Mark this conversation as being initialized
    initializingConversationRef.current = conversationId;
    setIsInitializing(true);
    setLoading(true);
    setError(null);

    try {
      // Cleanup previous listeners
      if (messageUnsubscribe) {
        messageUnsubscribe();
        setMessageUnsubscribe(null);
      }
      if (typingUnsubscribe) {
        typingUnsubscribe();
        setTypingUnsubscribe(null);
      }

      // For the hybrid architecture, we don't load messages from Django API
      // Instead, we'll get them directly from Firebase
      // First, we need to get conversation metadata to build the response structure
      // Try to use cached conversations first to avoid duplicate API calls
      let conversation = conversations.find(c => c.conversation_id === conversationId);
      
      if (!conversation) {
        // Only fetch if not found in cache
        const conversationsResponse = await getUserConversations();
        conversation = conversationsResponse.conversations.find(c => c.conversation_id === conversationId);
        // Update cache
        setConversations(conversationsResponse.conversations);
      }
      
      if (!conversation) {
        throw new ChatApiError('CONVERSATION_NOT_FOUND', 'Conversation not found', conversationId);
      }
      
      // Build a mock ConversationMessagesResponse structure for Firebase messages
      const conversationData: ConversationMessagesResponse = {
        conversation_id: conversationId,
        firebase_config: conversation.firebase_config,
        activity: conversation.activity,
        conversation_type: conversation.conversation_type,
        participants: [
          conversation.other_participant,
          {
            user_id: user?.uid || '',
            name: user?.displayName || '',
            email: user?.email || '',
            profile_picture: user?.photoURL || null,
            role: conversation.user_role === 'organizer' ? 'organizer' : 'participant'
          }
        ],
        messages: [], // Firebase will populate this via real-time subscription
        pagination: {
          hasMore: false,
          nextCursor: null
        },
        user_role: conversation.user_role,
        unread_count: conversation.unread_count || 0
      };
      
      setCurrentConversation(conversationData);

      // Update participant presence to online
      if (user?.uid) {
        await updateParticipantPresence(user.uid, true);
      }

      // Subscribe to real-time messages with proper debouncing
      const messagesUnsub = subscribeToMessages(conversationId, (messages) => {
        setRealtimeMessages(messages);
        
        // Auto-mark messages as read when they arrive (only once when conversation is opened)
        // No need to mark as read repeatedly as Firebase handles read receipts
        if (messages.length > 0) {
          console.log('📨 Messages received, Firebase will handle read receipts automatically');
        }
      });
      setMessageUnsubscribe(() => messagesUnsub);

      // Subscribe to typing indicators
      const typingUnsub = subscribeToTypingIndicators(conversationId, () => {
        // Typing state available if needed; currently not stored in context to avoid unused state
      });
      setTypingUnsubscribe(() => typingUnsub);

      // Mark conversation as read
      await markConversationAsRead(conversationId);
      
      // Update local conversations state to reflect read status
      setConversations(prev => prev.map(conv => 
        conv.conversation_id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));

      console.log('✅ Conversation opened successfully');
    } catch (error) {
      const errorMessage = error instanceof ChatApiError 
        ? error.message 
        : 'Failed to open conversation';
      setError(errorMessage);
      console.error('Error opening conversation:', error);
    } finally {
      setLoading(false);
      setIsInitializing(false);
      // Clear the initializing conversation ref
      initializingConversationRef.current = null;
    }
  }, [user, loadConversations, messageUnsubscribe, typingUnsubscribe, isInitializing]);

  /**
   * Send a message
   */
  const sendMessageHandler = useCallback(async (
    conversationId: string,
    message: string
  ) => {
    if (!user) return;

    try {
      // Send message directly to Firebase Realtime Database
      // Firebase Cloud Functions will handle notifications via Django API
      const trimmedMessage = message.trim();
      const messageId = await sendRealtimeMessage(conversationId, trimmedMessage);
      
      // Update local conversations state to reflect the new message
      const timestamp = new Date().toISOString();
      setConversations(prev => prev.map(conv => 
        conv.conversation_id === conversationId 
          ? { 
              ...conv, 
              last_message: {
                text: trimmedMessage,
                sender_name: user.displayName || user.email || 'You',
                timestamp
              }
            }
          : conv
      ));

      // Optimistically append message to real-time stream so UI reflects immediately
      setRealtimeMessages(prev => {
        const alreadyExists = prev.some(msg => msg.messageId === messageId);
        if (alreadyExists) {
          return prev;
        }

        const createdAt = Date.now();
        const optimisticMessage: ChatMessage = {
          messageId,
          senderId: user.uid,
          senderName: user.displayName || user.email || 'You',
          senderProfilePicture: user.photoURL || undefined,
          text: trimmedMessage,
          type: 'text',
          timestamp,
          createdAt,
          isEdited: false,
          isDeleted: false,
          editedAt: null,
          readBy: { [user.uid]: timestamp },
          imageUrl: null,
        };

        return [...prev, optimisticMessage].sort((a, b) => a.createdAt - b.createdAt);
      });
      
      console.log('✅ Message sent to Firebase successfully:', messageId);
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to send message';
      setError(errorMessage);
      console.error('Error sending message:', error);
      throw error;
    }
  }, [user]);

  /**
   * Mark conversation as read
   */
  const markAsRead = useCallback(async (conversationId: string) => {
    if (!user) return;

    try {
      await markConversationAsRead(conversationId);
      
      // Mark Firebase messages as read too
      if (realtimeMessages.length > 0) {
        await markAllMessagesAsRead(conversationId, realtimeMessages);
      }
      
      // Update local state instead of reloading all conversations
      setConversations(prev => prev.map(conv => 
        conv.conversation_id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
      
      console.log('✅ Conversation marked as read');
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [user, realtimeMessages]);

  /**
   * Refresh chat statistics based on current conversations
   */
  const refreshStats = useCallback(async () => {
    if (!user) return;

    try {
      // Calculate stats from current conversations to avoid additional API calls
      const mockStats: ChatStats = {
        total_conversations: conversations.length,
        active_conversations: conversations.filter(c => c.status === 'active').length,
        total_unread_messages: conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
        conversations_as_organizer: conversations.filter(c => c.user_role === 'organizer').length,
        conversations_as_participant: conversations.filter(c => c.user_role === 'participant').length,
      };
      
      setStats(mockStats);
      console.log('✅ Chat statistics refreshed from current conversations');
    } catch (error) {
      console.error('Error refreshing chat stats:', error);
    }
  }, [user, conversations]);

  /**
   * Subscribe to conversation (for external use)
   */
  const subscribeToConversation = useCallback((conversationId: string) => {
    openConversation(conversationId);
  }, [openConversation]);

  /**
   * Unsubscribe from current conversation
   */
  const unsubscribeFromConversation = useCallback(() => {
    if (messageUnsubscribe) {
      messageUnsubscribe();
      setMessageUnsubscribe(null);
    }
    if (typingUnsubscribe) {
      typingUnsubscribe();
      setTypingUnsubscribe(null);
    }
    
    setCurrentConversation(null);
    setRealtimeMessages([]);
    
    // Clear any pending initialization
    initializingConversationRef.current = null;
    setIsInitializing(false);
    
    console.log('✅ Unsubscribed from conversation');
  }, [messageUnsubscribe, typingUnsubscribe]);

  /**
   * Set typing status
   */
  const setTypingStatusHandler = useCallback(async (conversationId: string, isTyping: boolean) => {
    if (!user) return;
    
    try {
      await setTypingStatus(conversationId, isTyping);
    } catch (error) {
      console.error('Error setting typing status:', error);
    }
  }, [user]);

  // Auto-load conversations when user is authenticated
  useEffect(() => {
    if (!user) {
      // Reset state when user logs out
      setConversations([]);
      setCurrentConversation(null);
      setStats(null);
      unsubscribeFromConversation();
    }
    // Note: we intentionally avoid auto-loading conversations/stats here
    // to prevent background API calls when chat UI is not in use.
  }, [user]);

  // Merge real-time messages with current conversation
  const mergedMessages = currentConversation 
    ? [...currentConversation.messages, ...realtimeMessages.filter(msg => 
        !currentConversation.messages.some(apiMsg => apiMsg.messageId === msg.messageId)
      )].sort((a, b) => a.createdAt - b.createdAt)
    : realtimeMessages;

  const contextValue: ChatContextType = {
    // State
    conversations,
    currentConversation: currentConversation ? {
      ...currentConversation,
      messages: mergedMessages
    } : null,
    loading,
    error,
    stats,
    
    // Actions
    loadConversations,
    createConversation: createConversationHandler,
    openConversation,
    sendMessage: sendMessageHandler,
    markAsRead,
    refreshStats,
    
    // Real-time
    subscribeToConversation,
    unsubscribeFromConversation,
    setTypingStatus: setTypingStatusHandler
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use chat context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

// Helper hooks for specific chat functionality
export const useChatStats = () => {
  const { stats, refreshStats } = useChat();
  return { stats, refreshStats };
};

export const useConversations = () => {
  const { conversations, loadConversations, loading } = useChat();
  return { conversations, loadConversations, loading };
};

export const useCurrentConversation = () => {
  const { 
    currentConversation, 
    openConversation, 
    unsubscribeFromConversation,
    sendMessage,
    markAsRead 
  } = useChat();
  
  return { 
    currentConversation, 
    openConversation, 
    unsubscribeFromConversation,
    sendMessage,
    markAsRead 
  };
};
